import React, { useState, useEffect } from "react";
import { useStateContext } from "contexts/ContextProvider";
import { useWebSocket } from "contexts/WebSocketContext";
import {
  Box,
  useTheme,
  Typography,
  Button,
  Divider,
  Card,
  CardContent,
  useMediaQuery,
  Tabs,
  Tab,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import Grid from "@mui/material/Grid2";
import { useParams } from "react-router-dom";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import ToggleOffRoundedIcon from "@mui/icons-material/ToggleOffRounded";
import ToggleOnRoundedIcon from "@mui/icons-material/ToggleOnRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import { OVERLAY_NAMES } from "constants/Overlays";
import api from "services/api";
import { usePageTitle } from "hooks/usePageTitle";
import MemberActivity from "components/MemberActivity";
import TimesheetsTab from "../TimesheetsTab";
import HourlyRateHoverCell from "components/HourlyRateHoverCell";
import { NAVBAR_HEIGHT } from "constants/layout";

const MemberProfile = () => {
  const [pageTitle, setPageTitle] = useState("Member");
  usePageTitle(pageTitle);

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const navigate = useNavigate();
  const { id } = useParams();
  const { currentUser } = useAuth();
  const { socket } = useWebSocket();

  const [isLoading, setIsLoading] = useState(true);
  const [memberData, setMemberData] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [activeDetailsTab, setActiveDetailsTab] = useState(0);

  const { refreshPage } = useStateContext();
  const { setOverlayName, setOverlayData } = useStateContext();
  const { openDialog, handleErrorDialog, closeDialog, showSnackbar } =
    useStateContext();

  useEffect(() => {
    const fetchMemberData = async () => {
      setIsLoading(true);
      try {
        const response = await api.get(`/members/id/${id}`);
        setMemberData(response.data);
        setPageTitle(response.data.Name || "Member");
      } catch (error) {
        console.error("Error fetching member data:", error);
        handleErrorDialog("Failed to load member data", theme);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchMemberData();
    }
  }, [id, refreshPage, refreshTrigger, handleErrorDialog, theme]);

  // Listen for member updates
  useEffect(() => {
    if (!socket || !id) return;

    const handleMemberUpdate = (data) => {
      // Only refresh if this is the member being viewed
      if (data.recordId === parseInt(id)) {
        setRefreshTrigger((prev) => prev + 1);
        const userName = data.updatedBy?.userName || "Unknown User";
        const message = `Member ${data.changeAction} by ${userName}`;
        showSnackbar(message, theme, "info");
      }
    };

    socket.on("member:updated", handleMemberUpdate);
    return () => socket.off("member:updated", handleMemberUpdate);
  }, [socket, id, theme, showSnackbar]);

  // Listen for member updates
  useEffect(() => {
    if (!socket || !id) return;

    const handleMemberUpdate = (data) => {
      // Only refresh if this is the member being viewed
      if (data.recordId === parseInt(id)) {
        setRefreshTrigger((prev) => prev + 1);
        const userName = data.updatedBy?.userName || "Unknown User";
        const message = `Member ${data.changeAction} by ${userName}`;
        showSnackbar(message, theme, "info");
      }
    };

    socket.on("member:updated", handleMemberUpdate);
    return () => socket.off("member:updated", handleMemberUpdate);
  }, [socket, id, theme, showSnackbar]);

  const handleEdit = () => {
    const data = {
      MemberID: memberData.id,
      Member: memberData,
      Action: OVERLAY_NAMES.MEMBER_ACTION_EDIT,
    };

    setOverlayData(data);
    setOverlayName(OVERLAY_NAMES.MEMBER_NEW_EDIT);
  };

  const handleToggleMemberStatus = () => {
    const isActive = memberData?.Active !== false;
    const title = isActive ? "Disable Member" : "Enable Member";
    const message = isActive
      ? `Are you sure you want to disable ${memberData.Name}? They will no longer be able to login.`
      : `Are you sure you want to enable ${memberData.Name}? They will be able to login again.`;
    const primaryAction = isActive ? "Disable" : "Enable";

    openDialog({
      theme,
      title,
      message,
      primaryAction,
      secondaryAction: "Cancel",
      primaryOnClick: () => {
        // The API call fires ONLY here — on the dialog's Confirm button.
        // (Previously the request was created when the dialog OPENED, so the
        // button that opens the dialog also fired the enable/disable.)
        const request = isActive
          ? api.delete(`/members/${id}`)
          : api.patch(`/members/${id}/enable`);

        request
          .then(() => {
            closeDialog();
            showSnackbar(
              isActive
                ? "Member disabled successfully"
                : "Member enabled successfully",
              theme,
              "success",
            );

            // Emit socket event for real-time updates
            if (socket) {
              socket.emit("member:saved", {
                modelType: "Member",
                recordId: parseInt(id),
                changeAction: isActive ? "Disabled" : "Enabled",
                user: {
                  id: currentUser.id,
                  name: currentUser.Name,
                  imageThumbnail: currentUser.ImageThumbnail,
                },
              });
            }

            if (isActive) {
              navigate("/members");
            } else {
              setRefreshTrigger((prev) => prev + 1);
            }
          })
          .catch((err) => {
            console.error(err);
            closeDialog();
            handleErrorDialog(
              err.response?.data?.error ||
                (isActive ? "Error disabling member" : "Error enabling member"),
              theme,
            );
          });
      },
      secondaryOnClick: closeDialog,
    });
  };

  const handleSendEmail = () => {
    if (memberData && memberData.Email) {
      window.location.href = `mailto:${memberData.Email}`;
    }
  };

  if (isLoading) {
    return (
      <Box
        p="1rem"
        bgcolor={theme.palette.background.alt}
        sx={{
          flex: 1,
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="h4">Loading...</Typography>
      </Box>
    );
  }

  if (!memberData) {
    return (
      <Box
        p="1rem"
        bgcolor={theme.palette.background.alt}
        sx={{
          flex: 1,
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="h4">Member not found</Typography>
      </Box>
    );
  }

  return (
    <Box
      p="1rem 1rem 0 1rem"
      bgcolor={theme.palette.background.alt}
      sx={{
        flex: 1,
        height: `calc(100vh - ${NAVBAR_HEIGHT + (isMobile ? 0 : 20)}px)`,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Main Content */}
      <Box
        borderRadius="12px"
        //bgcolor={theme.palette.background.default}
        sx={{
          flexGrow: 1,
          //boxShadow: theme.shadows[3],
          height: "100%",
          display: "flex",
          flexDirection: "column",
          mb: 1,
          mx: 1,
          mt: 2,
        }}
      >
        <Grid container spacing={3}>
          {/* Member Information Card */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              sx={{
                bgcolor: theme.palette.background.default,
                borderRadius: "12px",
                height: "100%",
              }}
            >
              <CardContent>
                <Typography
                  variant={isMobile ? "h4" : "h3"}
                  fontWeight="bold"
                  gutterBottom
                >
                  Member Information
                </Typography>
                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Name
                  </Typography>
                  <Typography variant="body1">{memberData.Name}</Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Email
                  </Typography>
                  <Typography variant="body1">{memberData.Email}</Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Role
                  </Typography>
                  <Typography variant="body1">{memberData.Role}</Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Hourly Rate
                  </Typography>
                  {/* Privacy Mode aware: stays blurred on this device when
                      privacy mode is ON (hover/focus reveal is disabled). */}
                  <HourlyRateHoverCell
                    value={`$${Number.parseFloat(memberData.HourlyRate || 0).toFixed(2)}`}
                    variant="body1"
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Status
                  </Typography>
                  <Typography
                    variant="body1"
                    color={memberData.Active === false ? "error.main" : "success.main"}
                  >
                    {memberData.Active === false ? "Disabled" : "Active"}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Member ID
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {memberData.id}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Quick Actions Card */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              sx={{
                bgcolor: theme.palette.background.default,
                borderRadius: "12px",
                height: "100%",
              }}
            >
              <CardContent>
                <Typography
                  variant={isMobile ? "h4" : "h3"}
                  fontWeight="bold"
                  gutterBottom
                >
                  Quick Actions
                </Typography>
                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<EmailRoundedIcon />}
                    onClick={handleSendEmail}
                    fullWidth
                    sx={{
                      justifyContent: "flex-start",
                      borderRadius: "8px",
                      py: 1.5,
                    }}
                  >
                    Send Email
                  </Button>

                  <Button
                    variant="outlined"
                    startIcon={<EditRoundedIcon />}
                    onClick={handleEdit}
                    fullWidth
                    sx={{
                      justifyContent: "flex-start",
                      borderRadius: "8px",
                      py: 1.5,
                    }}
                  >
                    Edit Member
                  </Button>

                  {currentUser?.Role === "Admin" && (
                    <Button
                      variant="outlined"
                      color={memberData.Active === false ? "success" : "info"}
                      startIcon={
                        memberData.Active === false ? (
                          <ToggleOnRoundedIcon />
                        ) : (
                          <ToggleOffRoundedIcon />
                        )
                      }
                      onClick={handleToggleMemberStatus}
                      fullWidth
                      sx={{
                        justifyContent: "flex-start",
                        borderRadius: "8px",
                        py: 1.5,
                      }}
                    >
                      {memberData.Active === false ? "Enable Member" : "Disable Member"}
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Activity History */}
          <Grid size={{ xs: 12 }}>
            <Card
              sx={{
                bgcolor: theme.palette.background.default,
                borderRadius: "12px",
              }}
            >
              <CardContent>
                <Tabs
                  value={activeDetailsTab}
                  onChange={(_, nextValue) => setActiveDetailsTab(nextValue)}
                  variant={isMobile ? "fullWidth" : "standard"}
                  sx={{
                    minHeight: 42,
                    "& .MuiTab-root": {
                      minHeight: 42,
                      textTransform: "none",
                      fontWeight: 700,
                    },
                  }}
                >
                  <Tab label="Activity & Statistics" value={0} />
                  <Tab label="Timesheet" value={1} />
                </Tabs>
                <Divider sx={{ my: 2 }} />

                {activeDetailsTab === 0 && <MemberActivity memberId={id} />}

                {activeDetailsTab === 1 && (
                  <TimesheetsTab
                    isMobile={isMobile}
                    hideHeader
                    useOuterGridCard
                    hourlyRate={memberData?.HourlyRate}
                    memberId={memberData?.id}
                    memberName={memberData?.Name}
                    allowEdit={false}
                    inlineStatsAndFilter
                  />
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default MemberProfile;
